import { useState, useCallback } from 'react'

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Modal, Text } from '@mantine/core'
import { GripVertical } from 'lucide-react'

import { useMutation } from '@redwoodjs/web'

import DNButton from 'src/components/Buttons/Button/DNButton'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { REORDER_APPLICANT_FILTER_PRESETS } from 'src/graphql/applicantFilterPresets.graphql'

interface Preset {
  applicantFilterPresetId: string
  name: string
  order?: number | null
}

interface ManageViewsModalProps {
  opened: boolean
  onClose: () => void
  presets: Preset[]
  refetch: () => void
}

const SortablePresetItem = ({ preset }: { preset: Preset }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: preset.applicantFilterPresetId })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex flex-row items-center gap-3 rounded-md border px-3 py-2 ${
        isDragging
          ? 'border-doubleNickel-brand-300 bg-doubleNickel-brand-50'
          : 'border-doubleNickel-gray-200 bg-white'
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab text-doubleNickel-gray-400 hover:text-doubleNickel-gray-600 active:cursor-grabbing"
      >
        <GripVertical size={14} />
      </div>
      <Text className="text-sm font-medium text-doubleNickel-gray-800">
        {preset.name}
      </Text>
    </div>
  )
}

const ManageViewsModal = ({
  opened,
  onClose,
  presets,
  refetch,
}: ManageViewsModalProps) => {
  const [orderedPresets, setOrderedPresets] = useState<Preset[]>(presets)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [reorderPresets, { loading }] = useMutation(
    REORDER_APPLICANT_FILTER_PRESETS,
    {
      onCompleted: () => {
        toast('Views reordered', 'success')
        refetch()
        onClose()
      },
      onError: () => {
        toast('Failed to reorder views', 'error')
      },
    }
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (active.id !== over?.id) {
        setOrderedPresets((prev) => {
          const oldIndex = prev.findIndex(
            (p) => p.applicantFilterPresetId === active.id
          )
          const newIndex = prev.findIndex(
            (p) => p.applicantFilterPresetId === over.id
          )
          return arrayMove(prev, oldIndex, newIndex)
        })
      }
    },
    []
  )

  const handleSave = () => {
    const presetIds = orderedPresets.map((p) => p.applicantFilterPresetId)
    reorderPresets({
      variables: { presetIds },
    })
  }

  const hasChanges =
    JSON.stringify(orderedPresets.map((p) => p.applicantFilterPresetId)) !==
    JSON.stringify(presets.map((p) => p.applicantFilterPresetId))

  return (
    <Modal
      opened={opened}
      withCloseButton={false}
      onClose={onClose}
      closeOnClickOutside={true}
      closeOnEscape={true}
      yOffset={100}
      overlayProps={{
        backgroundOpacity: 0.2,
        blur: 5,
      }}
      classNames={{
        body: 'flex flex-col gap-4',
      }}
    >
      <Text className="text-sm font-semibold text-doubleNickel-gray-900">
        Manage Views
      </Text>
      <Text className="text-xs text-doubleNickel-gray-500">
        Drag to reorder your saved views. The order will be reflected in the tab
        bar.
      </Text>

      {orderedPresets.length === 0 ? (
        <Text className="py-4 text-center text-sm text-doubleNickel-gray-400">
          No saved views to reorder
        </Text>
      ) : (
        <div className="flex max-h-[400px] flex-col gap-2 overflow-y-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedPresets.map((p) => p.applicantFilterPresetId)}
              strategy={verticalListSortingStrategy}
            >
              {orderedPresets.map((preset) => (
                <SortablePresetItem
                  key={preset.applicantFilterPresetId}
                  preset={preset}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      )}

      <div className="flex flex-row items-center justify-end gap-2">
        <DNButton variant="outline" className="h-7" onClick={onClose}>
          Cancel
        </DNButton>
        <DNButton
          variant="primary"
          className="h-7"
          disabled={!hasChanges || loading}
          loading={loading}
          onClick={handleSave}
        >
          Save order
        </DNButton>
      </div>
    </Modal>
  )
}

export default ManageViewsModal
